import { useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ONBOARDING_ME_KEY, onboardingApi, type Checklist } from './onboarding.api';
import { QUOTINC_CHECKLIST_ID, QuotincChecklistLink } from './QuotincOnboarding';
import './onboarding.css';

/**
 * The getting-started list on the Hub home: the admin's for a workspace being
 * set up, the lighter one for an invited colleague. Every line is read from
 * the real state by the API (nothing is ticked by hand) and the card leaves
 * by itself once every required line is done, or when its owner hides it.
 * Never shown for Asian Trails' existing workspace and accounts.
 */
export function GettingStartedCard({ checklist }: { checklist: Checklist }) {
  const qc = useQueryClient();
  const [hiding, setHiding] = useState(false);
  const required = checklist.items.filter((i) => !i.optional);
  const done = required.filter((i) => i.done).length;
  const pct = required.length ? Math.round((done / required.length) * 100) : 100;
  const workspace = checklist.kind === 'workspace';

  const hide = async () => {
    setHiding(true);
    try {
      await onboardingApi.dismissChecklist(checklist.kind);
      await qc.invalidateQueries({ queryKey: ONBOARDING_ME_KEY });
    } finally {
      setHiding(false);
    }
  };

  return (
    <section className="gs" aria-labelledby="gs-title" data-tour="checklist">
      <header className="gs__head">
        <div>
          <p className="onb-eyebrow">getting started</p>
          <h2 id="gs-title" className="gs__title">
            {workspace ? 'Set up your workspace' : 'Finish settling in'}
          </h2>
        </div>
        <div className="gs__meta">
          <span className="gs__count">
            {done} of {required.length} done
          </span>
          {workspace && (
            <Link to="/admin/setup" className="onb-btn">
              Open setup
            </Link>
          )}
        </div>
      </header>
      <div className="gs__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Progress">
        <span style={{ width: `${pct}%` }} />
      </div>
      <ol className="gs__list">
        {checklist.items.map((item) => (
          <li key={item.id} className={item.done ? 'is-done' : undefined}>
            <span className="gs__tick" aria-hidden>
              {item.done ? (
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <path d="M2.5 6.2 L5 8.6 L9.6 3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null}
            </span>
            <span className="gs__text">
              {item.done ? (
                <span className="gs__label">{item.label}</span>
              ) : item.id === QUOTINC_CHECKLIST_ID ? (
                <QuotincChecklistLink
                  className="gs__label"
                  fallback={
                    <Link to={item.to} className="gs__label">
                      {item.label}
                    </Link>
                  }
                >
                  {item.label}
                </QuotincChecklistLink>
              ) : (
                <Link to={item.to} className="gs__label">
                  {item.label}
                </Link>
              )}
              <span className="gs__hint">
                {item.hint}
                {item.optional ? ' Optional.' : ''}
              </span>
            </span>
            <span className="sr-only">{item.done ? 'done' : 'to do'}</span>
          </li>
        ))}
      </ol>
      <footer className="gs__foot">
        <span className="onb-hint">Each line ticks by itself once it is really done.</span>
        <button type="button" className="onb-link" onClick={() => void hide()} disabled={hiding}>
          Hide this list
        </button>
      </footer>
    </section>
  );
}
